import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import CardHeader from '@/components/Card/CardHeader';
import LongMenu from '@/components/LongMenu/LongMenu';
import { useStyles } from '@/styles/templateInputStyles';
import {
  checkOrganizationFeatures,
  CHECK_TYPE,
  ENTITY_TYPE,
  ORGANIZATION_FEATURES,
  PAYMENT_METHODS,
  REFUND_METHOD,
  REFUND_STATUSES,
  REFUND_SUB_STATUS,
  REFUND_TYPES,
  SYSTEM_TAGS,
  TRANSACTION_STATUSES,
  utcToLocal,
} from '@/utility';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import {
  ArrowDownward,
  ArrowUpward,
  AttachMoneyRounded,
  EditRounded,
  HistoryRounded,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow, { tableRowClasses } from '@mui/material/TableRow';
import axios from 'axios';
import { Base64 } from 'js-base64';
import fileDownload from 'js-file-download';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { useGetRefunds } from '../../../api/hooks';
import { RefundsApi } from '../../../api/clients/refunds/refunds.api';
import { TransactionsApi } from '../../../api/clients/transactions/transactions.api';
import loader from '../../../assets/img/loader.svg';
import CancelPaymentDialog from '@/components/CancelPaymentDialog/CancelPaymentDialog';
import Button from '../../../components/CustomButtons/Button';
import { API_BASE_URL, CLAIM_CLIENT_URL } from '../../../config';
import IssueCheckModal from '../IssueCheckModal';
import RefundInfoHistory from '../RefundInfoHistory';
import UpdateRefundModal from '../UpdateRefundModal';
import EditManualRefundModal from '../../Facilities/EditManualRefundModal';
import { useStylesDanger } from './hooks';
import { StatusHistoryModal } from './StatusHistory/StatusHistoryModal';
import CheckboxDropdown from '@/components/CheckboxDropdown/CheckboxDropdown';
import { useLocation } from 'react-router-dom';
import CustomInput from '@/components/CustomInput/CustomInput';
import ConfirmationModal from '@/components/ConfirmationModal';
import PayeeCategorySelect from '@/components/PayeeCategorySelect';
import useTranslatedTransactionStatuses from '@/hooks/useTranslatedTransactionStatuses';
import AchReturnedChip from '@/components/AchReturnedChip/AchReturnedChip';
import FacilitySelect from '@/components/FacilitySelect';
import { getBusinessUnitsForForm } from '@/api/clients/business-units/business-units.api';
import { useGetFacilityGroupsForForms } from '@/api/hooks/facility-groups/useGetFacilityGroupsForForms';
import { useConfirmation } from '@/context/ConfirmationContext';
import CustomPaymentTypeSelect from '@/components/CustomPaymentTypeSelect';
import ECheckPreviewModal from '@/components/ECheckPreviewModal/ECheckPreviewModal';

const StyledTableRow = styled(TableRow)(({ payment, role }) => {
  return {
    [`&.${tableRowClasses.root}`]: {
      backgroundColor:
        payment.transaction_status === 'ACH-returned' && role === 'admin'
          ? '#eaa4a4'
          : undefined,
    },
  };
});

const checkStatusOptions = [
  TRANSACTION_STATUSES.PENDING,
  TRANSACTION_STATUSES.SENT_UNCASHED,
  TRANSACTION_STATUSES.VOIDED,
];
const achStatusOptions = [
  TRANSACTION_STATUSES.PENDING,
  TRANSACTION_STATUSES.ACH_SENT,
  TRANSACTION_STATUSES.ACH_RETURNED,
  TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK,
  TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM,
];
const tangoStatusOptions = [TRANSACTION_STATUSES.PENDING];

const refundTypeToLabel = {
  [REFUND_TYPES.REGULAR]: 'Consumer Payment',
  [REFUND_TYPES.INSURANCE]: 'Insurance Payment',
  [REFUND_TYPES.BUSINESS]: 'Business Payment',
};

const COMMUNICATION_EXCEPTIONS = Object.freeze({
  INVALID_ADDRESS: 'Invalid Address',
  UNDELIVERABLE: 'Undeliverable',
});

export default function FacilityTransaction({ role }) {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const isCustomPaymentTypesEnabled =
    role !== 'admin' &&
    posthog?.isFeatureEnabled('new-custom-payment-types-workflow');
  const { getTranslatedStatus } = useTranslatedTransactionStatuses();
  const { requestConfirmation } = useConfirmation();
  const { facilityGroups } = useGetFacilityGroupsForForms({
    enabled: role === 'admin',
  });

  const [selectedRefund, setSelectedRefund] = useState(null);
  const [currentInfo, setCurrentInfo] = useState(null);
  const [originalInfo, setOriginalInfo] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [modalErr, setModalErr] = useState('');
  const [currentCheckRefund, setCurrentCheckRefund] = useState(null);
  const [selectedForTrackChangesId, setSelectedForTrackChangesId] = useState(
    null,
  );
  const [businessUnits, setBusinessUnits] = useState([]);
  const [transactionStatuses, setTransactionStatuses] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [dataMappings, setDataMappings] = useState([]);
  const [dataMappingOptions, setDataMappingOptions] = useState([]);

  const isInsuranceEnabled = useMemo(() => {
    return allowedRefundTypes.includes(REFUND_TYPES.INSURANCE);
  }, [allowedRefundTypes]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      const businessUnitsData = await getBusinessUnitsForForm();
      setBusinessUnits(businessUnitsData);
      setFilteredBusinessUnits(businessUnitsData);

      if (role !== 'admin') {
        const response = await RefundsApi.getAllowedRefundTypes();
        setAllowedRefundTypes(response.data.allowed_refund_types);
      }
    };
    fetchBusinessUnits();
  }, []);

  useEffect(() => {
    const fetchDataMappings = async () => {
      try {
        const response = await axios.get(
          API_BASE_URL +
            '/data-mappings/for-filters' +
            (role === 'admin' ? '-admin' : ''),
        );
        setDataMappings(response.data);
      } catch (error) {
        console.error('Error fetching data mappings:', error);
      }
    };

    fetchDataMappings();
  }, []);

  const [infoHistory, setInfoHistory] = useState([]);
  const classes = useStyles();
  const dangerClass = useStylesDanger();

  const listInnerRef = useRef();
  const location = useLocation();

  const getQuickFilterDefaults = (quickFilterValue) => {
    if (!quickFilterValue) return {};
    if (quickFilterValue === 'Flagged Records') {
      return {
        quickFilter: quickFilterValue,
        isError: true,
        hasFilesAttached: false,
      };
    }
    if (quickFilterValue === 'All Returned') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.REQUESTED,
        transactionStatus: TRANSACTION_STATUSES.ACH_RETURNED,
        paymentMethod: '7',
        subStatus: '',
        payeeCategory: '',
        additionalInfo: '',
        hasFilesAttached: false,
      };
    }
    if (quickFilterValue === 'All Voided') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.REQUESTED,
        transactionStatus: TRANSACTION_STATUSES.VOIDED,
        paymentMethod: '5',
        subStatus: '',
        payeeCategory: '',
        additionalInfo: '',
        hasFilesAttached: false,
      };
    }
    if (quickFilterValue === 'Payee Exceptions') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.EXCEPTION,
        transactionStatus: '',
        subStatus: REFUND_SUB_STATUS.PAYEE_EXCEPTION,
        payeeCategory: 'insurance',
        additionalInfo: '',
        hasFilesAttached: false,
      };
    }
    if (quickFilterValue === 'Payee Exceptions with PDF') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.EXCEPTION,
        transactionStatus: '',
        subStatus: REFUND_SUB_STATUS.PAYEE_EXCEPTION,
        payeeCategory: 'insurance',
        additionalInfo: '',
        hasFilesAttached: true,
      };
    }
    if (quickFilterValue === 'Addenda Missing') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.EXCEPTION,
        transactionStatus: '',
        subStatus: REFUND_SUB_STATUS.ADDENDA_MISSING,
        payeeCategory: 'insurance',
        additionalInfo: '',
        hasFilesAttached: false,
      };
    }
    if (quickFilterValue === 'Invalid Address') {
      return {
        quickFilter: quickFilterValue,
        status: REFUND_STATUSES.EXCEPTION,
        transactionStatus: '',
        subStatus: REFUND_SUB_STATUS.COMMUNICATION,
        payeeCategory: '',
        additionalInfo: COMMUNICATION_EXCEPTIONS.INVALID_ADDRESS,
        hasFilesAttached: false,
      };
    }
    return {};
  };

  const queryParams = new URLSearchParams(location.search);
  const quickFilterFromQuery = queryParams.get('quickFilter');
  const quickFilterDefaults = useMemo(
    () => getQuickFilterDefaults(quickFilterFromQuery),
    [quickFilterFromQuery],
  );

  const {
    payments,
    loading,
    setPartialState,
    startDate,
    endDate,
    payeeCategory,
    status,
    dateFilterType,
    facility,
    handleSearch,
    paymentMethod,
    subStatus,
    selectedBusinessUnit,
    transactionStatus,
    additionalInfo,
    offSet,
    hasMore,
    sortBy,
    refetchRefunds,
    search,
    selectedDataMappings,
    quickFilter,
    hasFilesAttached,
    customPaymentTypeId,
    facilityGroupId,
  } = useGetRefunds({ role, defaultValues: quickFilterDefaults });

  useEffect(() => {
    if (paymentMethod) {
      const transactionStatusesToUse =
        paymentMethod == 7
          ? achStatusOptions
          : paymentMethod == 6
          ? tangoStatusOptions
          : paymentMethod == 4
          ? tangoStatusOptions
          : paymentMethod == 5
          ? checkStatusOptions
          : tangoStatusOptions;

      setTransactionStatuses(transactionStatusesToUse);
    }
  }, [paymentMethod]);

  useEffect(() => {
    const mappings = dataMappings
      ?.filter((dataMapping) =>
        facility && role === 'admin'
          ? dataMapping.facility_id === +facility
          : true,
      )
      .map((mapping) => ({
        value: mapping.id,
        label: mapping.name,
      }));
    setDataMappingOptions(mappings);
  }, [dataMappings, facility]);

  useEffect(() => {
    if (currentCheckRefund) {
      setCurrentCheckRefund(
        payments?.find((payment) => payment.id === currentCheckRefund.id) ??
          null,
      );
    }
  }, [currentCheckRefund, payments]);

  const onSearchChange = (event) => {
    setPartialState({ search: event.target.value });
    handleSearch(event.target.value);
  };

  const selectStatus = (event) => {
    setPartialState({
      transactionStatus: '',
      subStatus: '',
      status: event.target.value,
      paymentMethod: '',
      quickFilter: '',
    });
  };

  const changeDate = (event) => {
    setPartialState({ startDate: event ?? '' });
  };

  const changeEndDate = (event) => {
    setPartialState({ endDate: event ?? '' });
  };

  const sortByColumn = (columnName) => {
    setPartialState({
      sortBy: {
        name: columnName,
        order: sortBy.name === columnName ? (sortBy.order === 1 ? -1 : 1) : 1,
      },
    });
  };

  const sortArrow = (columnName) => {
    return columnName === sortBy.name ? (
      sortBy.order === 1 ? (
        <ArrowDownward fontSize='small' />
      ) : sortBy.order === -1 ? (
        <ArrowUpward fontSize='small' />
      ) : sortBy.order === 0 ? (
        ''
      ) : (
        ''
      )
    ) : (
      ''
    );
  };

  const onQuickFilterChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setPartialState({
        quickFilter: '',
        status: '',
        transactionStatus: '',
        paymentMethod: '',
        subStatus: '',
        payeeCategory: '',
        additionalInfo: '',
        isError: false,
      });
      return;
    }
    setPartialState(getQuickFilterDefaults(value));
  };

  const fetchStatusHistory = async () => {
    if (!selectedRefund) {
      setStatusHistory([]);
      return;
    }

    const fetchedStatusHistory = (
      await axios.get(API_BASE_URL + `/status_history/` + selectedRefund)
    ).data;

    if (fetchedStatusHistory.ach_data_backfill_error) {
      toast.error(fetchedStatusHistory.ach_data_backfill_error);
    }

    setStatusHistory(fetchedStatusHistory.history);
  };

  useEffect(() => {
    fetchStatusHistory();
  }, [selectedRefund]);

  const handleRetryPullingAch = async () => {
    fetchStatusHistory();
  };

  const copyLinkToClipBoard = (id) => {
    const encodedId = Base64.encode(id);
    navigator.clipboard.writeText(CLAIM_CLIENT_URL + '/payments/' + encodedId);
    toast.success(t('payments.toast.copiedToClipboard'), { autoClose: 1000 });
  };
  const selectedPayments = payments.filter((payment) => payment.isChecked);

  const onInputChange = (e) => {
    if (e.target.type === 'text' || e.target.type === 'email') {
      setCurrentInfo((currentInfo) => {
        const info = { ...currentInfo };
        info[e.target.name] = e.target.value;
        return info;
      });
    }
    if (e.target.type === 'checkbox') {
      setCurrentInfo((currentInfo) => {
        const info = { ...currentInfo };
        info[e.target.name] = e.target.checked;
        return info;
      });
    }
  };
  const processPayment = async ({ id, skipCheck = false, checkType }) => {
    try {
      const encodedId = Base64.encode(id);
      await axios.post(API_BASE_URL + '/add_check_facility/' + encodedId, {
        skipCheck: skipCheck,
        checkType,
      });
      setCurrentCheckRefund(null);
      refetchRefunds();

      if (checkType === CHECK_TYPE.E_CHECK) {
        requestConfirmation(
          t('payments.messages.echeckProcessStarted'),
          () => {},
        );
      }
    } catch (error) {
      console.error(
        'Error processing payment:',
        error.response?.data?.message,
        error.response?.status,
      );
      if (
        error.response?.status === 400 &&
        error.response?.data?.invalidAddress === true
      ) {
        const message = (
          <div>
            {t('payments.addressValidation.failed')}{' '}
            {error.response?.data?.invalidAddressMessage}
            <br />
            <br />
            {t('payments.addressValidation.proceedWarning')}
          </div>
        );
        requestConfirmation(message, () =>
          processPayment({ id, skipCheck: true, checkType }),
        );
      } else {
        console.error('Error processing payment:', error);
      }
    }
  };

  const getInfoHistory = async (id, refund) => {
    try {
      const result = await axios.get(
        API_BASE_URL + `/get_refund_info_history/` + id,
      );
      const infoHistoryArray = [];
      let lastRefundInfoIndex;

      const lastRefundInfo = result.data
        .filter((info) => info.type === 'refund_info')
        .at(-1);

      lastRefundInfoIndex = result.data.findIndex(
        (info) => lastRefundInfo?.id === info.id,
      );

      result.data.forEach((info, index) => {
        if (info.type === 'refund_attachments') {
          const oldFiles = info.old_info ?? [];
          const newFiles = info.new_info ?? [];
          const newFilesKeys = newFiles.map((file) => file.aws_key);
          const oldFilesKeys = oldFiles.map((file) => file.aws_key);

          const filesAdded = newFiles.filter(
            (file) => !oldFilesKeys.includes(file.aws_key),
          );

          const filesRemoved = oldFiles.filter(
            (file) => !newFilesKeys.includes(file.aws_key),
          );

          infoHistoryArray.push({
            filesAdded,
            filesRemoved,
            type: info.type,
            updated_by: `${info.updated_by} on ${moment(info.created_at).format(
              'YYYY-MM-DD HH:mm:ss',
            )}`,
            updated_at_location: info.updated_at_location,
          });
        }

        infoHistoryArray.push({
          first_name: info.new_info.first_name,
          last_name: info.new_info.last_name,
          email: info.new_info.email,
          address: `${info.new_info.refund_to_address_line1},
${info.new_info.refund_to_address_line2},
${info.new_info.refund_to_city},
${info.new_info.refund_to_state}, ${info.new_info.refund_to_zip}.`,
          comment: info.comment ?? 'N/A',
          updated_by: `${info.updated_by} on ${moment(info.created_at).format(
            'YYYY-MM-DD HH:mm:ss',
          )}`,
          type: info.type,
          id: info.id,
          memo: info.new_info.memo,
          business_payee_name: info.new_info.business_payee_name,
          updated_at_location: info.updated_at_location,
        });

        if (lastRefundInfoIndex === index) {
          infoHistoryArray.push({
            first_name: info.old_info.first_name,
            last_name: info.old_info.last_name,
            email: info.old_info.email,
            address: `${info.old_info.refund_to_address_line1},
${info.old_info.refund_to_address_line2},
${info.old_info.refund_to_city},
${info.old_info.refund_to_state}, ${info.old_info.refund_to_zip}.`,
            comment: 'N/A',
            updated_by: 'N/A',
            type: info.type,
            id: info.id,
            memo: info.old_info.memo,
            business_payee_name: info.old_info.business_payee_name,
            updated_at_location: 'N/A',
          });
        }
      });

      const refundInfoLength = infoHistoryArray.filter(
        (info) => info.type === 'refund_info',
      ).length;

      if (!refundInfoLength) {
        infoHistoryArray.push({
          first_name: refund.first_name,
          last_name: refund.last_name,
          email: refund.email,
          address: `${refund.refund_to_address_line1},
${refund.refund_to_address_line2},
${refund.refund_to_city},
${refund.refund_to_state}, ${refund.refund_to_zip}.`,
          comment: 'N/A',
          updated_by: 'N/A',
          type: 'refund_info',
          memo: refund.memo,
          business_payee_name: refund.business_payee_name,
          updated_at_location: 'N/A',
        });
      }

      setInfoHistory(infoHistoryArray);
    } catch (err) {
      console.log(err);
    }
  };

  const getClass = (info, index, input) => {
    const refundInfoHistory = infoHistory.filter(
      (info) => info.type === 'refund_info',
    );

    const historyItem = infoHistory[index];

    const infoHistoryIndex = refundInfoHistory.findIndex((historyInfo) => {
      return historyInfo.id === historyItem.id;
    });

    if (infoHistoryIndex === refundInfoHistory.length - 1) {
      return classes.root;
    }

    return info[input] != refundInfoHistory[infoHistoryIndex + 1][input]
      ? dangerClass.root
      : classes.root;
  };

  const handleScroll = async () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      // INFO: consider margin of error, 10 px
      const sumPx = scrollTop + clientHeight;
      const margin = 10;
      if (sumPx > scrollHeight - margin && sumPx < scrollHeight + margin) {
        if (hasMore && !loading) {
          setPartialState({ offSet: offSet + 25 });
        }
      }
    }
  };

  const downloadFile = useCallback(async () => {
    try {
      const result = await axios.get(
        API_BASE_URL +
          `/downloadReport?filters=` +
          JSON.stringify({
            search: search,
            refundMethod: paymentMethod || undefined,
            facility_id: facility,
            refundStatus: status || undefined,
            orderByColumn: sortBy.name,
            orderType: sortBy.order == 1 ? 'ASC' : 'DESC',
            startDate,
            endDate,
            business_unit_id: selectedBusinessUnit,
            additionalInfo,
            isError: quickFilter === 'Flagged Records',
            subStatus,
            dateFilterType,
            transactionStatus,
            refundType: payeeCategory,
            dataMappings: selectedDataMappings?.length
              ? selectedDataMappings
              : undefined,
            hasFilesAttached: hasFilesAttached,
          }),
      );
      fileDownload(result.data, 'RefundReport.csv');
    } catch (err) {
      console.error(err);
    }
  }, [
    role,
    search,
    paymentMethod,
    facility,
    status,
    sortBy,
    startDate,
    endDate,
    selectedBusinessUnit,
    additionalInfo,
    quickFilter,
    subStatus,
    dateFilterType,
    transactionStatus,
    payeeCategory,
    selectedDataMappings,
  ]);

  const handleClickDownload = useCallback(() => {
    toast.success(t('payments.toast.downloadingReport'), {
      position: 'bottom-right',
      autoClose: 5000,
    });
    downloadFile();
  }, [downloadFile]);

  const updatePaymentMethod = (e) => {
    setPartialState({ paymentMethod: e.target.value, quickFilter: '' });
    const transactionStatusesToUse =
      e.target.value == 7
        ? achStatusOptions
        : e.target.value == 6
        ? tangoStatusOptions
        : e.target.value == 4
        ? tangoStatusOptions
        : e.target.value == 5
        ? checkStatusOptions
        : tangoStatusOptions;

    setTransactionStatuses(transactionStatusesToUse);
  };

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelComment, setCancelComment] = useState('');
  const [cancelRefundId, setCancelRefundId] = useState(null);

  const confirmCancelRefund = async () => {
    if (!cancelRefundId || !cancelComment) return;
    try {
      await axios.post(API_BASE_URL + `/cancelRefund/${cancelRefundId}`, {
        comment: cancelComment,
      });
      setCancelModalOpen(false);
      setCancelComment('');
      setCancelRefundId(null);
      refetchRefunds();
    } catch (err) {
      console.log('Error canceling refund:', err);
    }
  };

  const [cancelPaymentModalOpen, setCancelPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedECheckRefundId, setSelectedECheckRefundId] = useState(null);

  const handleCancelPaymentConfirm = async (comment) => {
    await TransactionsApi.cancelPayment(selectedPayment.id, comment);
    toast.success(t('payments.messages.cancelPaymentSuccess'));
    setCancelPaymentModalOpen(false);
    setSelectedPayment(null);
    refetchRefunds();
  };

  const statusAction = async ({ path, id, comment }) => {
    if (path === 'cancelPayment') {
      const payment = payments.find((r) => r.id === id);
      setSelectedPayment(payment);
      setCancelPaymentModalOpen(true);
      return;
    }
    if (path.includes('cancel')) {
      setCancelRefundId(id);
      setCancelModalOpen(true);
      return;
    }
    await axios.get(API_BASE_URL + '/' + path + '/' + id, {
      params: { comment },
    });
    refetchRefunds();
  };

  const onFacilitySelect = (value) => {
    setPartialState({ facility: value });
    setFilteredBusinessUnits(
      businessUnits.filter((businessUnit) => businessUnit.facility_id == value),
    );
  };

  const toggleDelete = async (refundId) => {
    await axios.get(API_BASE_URL + `/toggle_delete/` + refundId);
    refetchRefunds();
  };

  return (
    <div>
      <Card>
        <CardHeader
          style={{ display: 'flex', justifyContent: 'space-between' }}
          color='primary'>
          <h4>{t('payments.title')} </h4>
        </CardHeader>

        <CardBody>
          <div
            ref={listInnerRef}
            style={{ maxHeight: '75vh', overflow: 'auto' }}
            onScroll={handleScroll}>
            {loading && (
              <div className='loaders'>
                <img src={loader} />
              </div>
            )}

            <div className='t-header'>
              <div className='t-header-left' style={{ marginTop: 0 }}>
                <div className='custom-search-box'>
                  <div
                    className='custom-select'
                    style={{ marginTop: 0, paddingBottom: 0 }}>
                    <select
                      id='status'
                      className='full-width-elem'
                      onChange={(e) =>
                        setPartialState({ dateFilterType: e.target.value })
                      }>
                      <option value='created_at'>
                        {t('payments.dateFilters.dateProcessed')}
                      </option>
                      <option value='status_updatedat'>
                        {t('payments.dateFilters.lastUpdated')}
                      </option>
                    </select>
                  </div>
                  <span className='search-icon'></span>
                </div>
              </div>
              <div className='t-header-left' style={{ marginTop: 0 }}>
                {t('payments.dateFilters.fromLabel')}&nbsp;&nbsp;
                <DatePicker
                  className='full-width-elem-date-picker'
                  id='dt'
                  autoComplete='off'
                  selected={startDate}
                  onChange={changeDate}
                />
              </div>
              <div className='t-header-left' style={{ marginTop: 0 }}>
                {t('payments.dateFilters.toLabel')}&nbsp;&nbsp;
                <DatePicker
                  className='full-width-elem-date-picker'
                  id='dt'
                  autoComplete='off'
                  selected={endDate}
                  onChange={changeEndDate}
                />
              </div>

              <div className='t-header-right'>
                <div className='custom-search-box'>
                  <input
                    type='search'
                    className='seacrh-field'
                    value={search}
                    onChange={onSearchChange}
                    placeholder={t('payments.search.placeholder')}
                  />
                  <span className='search-icon'>
                    <SearchIcon />
                  </span>
                </div>
              </div>
            </div>
            <div className='t-header'>
              {(role === 'admin' || !isCustomPaymentTypesEnabled) && (
                <div
                  className='custom-select'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  {allowedRefundTypes.length > 1 || role === 'admin' ? (
                    <PayeeCategorySelect
                      disablePadding
                      role={role}
                      allowedRefundTypes={allowedRefundTypes}
                      payeeCategory={payeeCategory}
                      onChange={(e) =>
                        setPartialState({
                          payeeCategory: e.target.value,
                          quickFilter: '',
                        })
                      }
                    />
                  ) : null}
                </div>
              )}
              {(role === 'admin' || isCustomPaymentTypesEnabled) && (
                <CustomPaymentTypeSelect
                  value={customPaymentTypeId}
                  onChange={(e) =>
                    setPartialState({
                      customPaymentTypeId: e.target.value,
                      quickFilter: '',
                    })
                  }
                />
              )}

              <div
                className='t-header-left'
                style={{ marginTop: 0, paddingBottom: 0 }}>
                <div className='custom-search-box'>
                  <div
                    className='custom-select'
                    style={{ marginTop: 0, paddingBottom: 0 }}>
                    <select
                      id='status'
                      className='full-width-elem'
                      value={status}
                      onChange={selectStatus}>
                      <option value=''>
                        {t('payments.filters.refundStatus')}
                      </option>
                      {Object.values(REFUND_STATUSES).map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className='search-icon'></span>
                </div>
              </div>

              {status === REFUND_STATUSES.UNCLAIMED && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={subStatus}
                        onChange={(e) =>
                          setPartialState({
                            subStatus: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.subStatus')}
                        </option>
                        <option value={REFUND_SUB_STATUS.EMAIL_SENT}>
                          {t('payments.subStatus.emailSent')}
                        </option>
                        <option value={REFUND_SUB_STATUS.COMMUNICATION_PENDING}>
                          {t('payments.subStatus.communicationPending')}
                        </option>
                        <option value={REFUND_SUB_STATUS.MAIL_SENT}>
                          {t('payments.subStatus.mailSent')}
                        </option>
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {status === REFUND_STATUSES.CANCELED && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={subStatus}
                        onChange={(e) =>
                          setPartialState({
                            subStatus: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.subStatusPlaceholder')}
                        </option>
                        <option value={REFUND_SUB_STATUS.TRANSFER_PENDING}>
                          {t('common.subStatus.transferPending')}
                        </option>
                        <option value={REFUND_SUB_STATUS.TRANSFER_COMPLETE}>
                          {t('common.subStatus.transferComplete')}
                        </option>
                        <option value={REFUND_SUB_STATUS.TRANSFER_FAILED}>
                          {t('common.subStatus.transferFailed')}
                        </option>
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {status === REFUND_STATUSES.EXCEPTION && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={subStatus}
                        onChange={(e) =>
                          setPartialState({
                            subStatus: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.subStatusPlaceholder')}
                        </option>
                        <option value={REFUND_SUB_STATUS.COMMUNICATION}>
                          {t('payments.subStatus.communication')}
                        </option>
                        <option value={REFUND_SUB_STATUS.PAYEE_EXCEPTION}>
                          {t('payments.subStatus.payeeException')}
                        </option>
                        <option value={REFUND_SUB_STATUS.ADDENDA_MISSING}>
                          {t('payments.subStatus.addendaMissing')}
                        </option>
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {subStatus === REFUND_SUB_STATUS.COMMUNICATION && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={additionalInfo}
                        onChange={(e) =>
                          setPartialState({
                            additionalInfo: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.subStatusPlaceholder')}
                        </option>
                        {Object.values(COMMUNICATION_EXCEPTIONS).map(
                          (status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {status === REFUND_STATUSES.ESCHEATED && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={subStatus}
                        onChange={(e) =>
                          setPartialState({
                            subStatus: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.subStatusPlaceholder')}
                        </option>
                        <option
                          value={REFUND_SUB_STATUS.DORMANT_PENDING_ESCHEATMENT}>
                          {t('payments.subStatus.dormantPendingEscheatment')}
                        </option>
                        <option
                          value={REFUND_SUB_STATUS.ESCHEATMENT_EMAIL_SENT}>
                          {t('payments.subStatus.escheatmentEmailSent')}
                        </option>
                        <option
                          value={
                            REFUND_SUB_STATUS.ESCHEATMENT_PHYSICAL_MAIL_SENT
                          }>
                          {t('payments.subStatus.escheatmentPhysicalMailSent')}
                        </option>
                        <option
                          value={
                            REFUND_SUB_STATUS.ESCHEATMENT_CERTIFIED_MAIL_SENT
                          }>
                          {t('payments.subStatus.escheatmentCertifiedMailSent')}
                        </option>
                        <option
                          value={
                            REFUND_SUB_STATUS.ESCHEATMENT_NUAPA_CLIENT_REVIEW
                          }>
                          {t('payments.subStatus.escheatmentNuapaClientReview')}
                        </option>
                        <option value={REFUND_SUB_STATUS.ESCHEATED_TO_STATE}>
                          {t('payments.subStatus.escheatedToState')}
                        </option>
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {(status === REFUND_STATUSES.COMPLETED ||
                status === REFUND_STATUSES.REQUESTED) && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={paymentMethod}
                        onChange={updatePaymentMethod}>
                        <option value=''>
                          {t('payments.filters.paymentTypePlaceholder')}
                        </option>
                        {PAYMENT_METHODS.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.text}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              {status === REFUND_STATUSES.REQUESTED && paymentMethod && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        value={transactionStatus}
                        onChange={(e) =>
                          setPartialState({
                            transactionStatus: e.target.value,
                            quickFilter: '',
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.transactionStatusPlaceholder')}
                        </option>
                        {transactionStatuses.map((method) => (
                          <option key={method} value={method}>
                            {getTranslatedStatus(method)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}

              <FacilitySelect
                value={facility}
                onChange={onFacilitySelect}
                parentKey='transaction_page'
                childKey='refund_information'
                permission='read'
              />

              {role === 'admin' && (
                <div style={{ marginTop: 0, paddingBottom: 0, marginLeft: 10 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='facilityGroup'
                        className='full-width-elem'
                        value={facilityGroupId}
                        style={{ height: '35px' }}
                        onChange={(e) =>
                          setPartialState({
                            facilityGroupId: e.target.value,
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.facilityGroupPlaceholder')}
                        </option>
                        {facilityGroups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.organization_name} - {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}

              {(checkOrganizationFeatures(
                ORGANIZATION_FEATURES.showBusinessUnitsPage,
              ) ||
                role === 'admin') && (
                <div
                  className='t-header-left'
                  style={{ marginTop: 0, paddingBottom: 0 }}>
                  <div className='custom-search-box'>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      <select
                        id='status'
                        className='full-width-elem'
                        onChange={(e) =>
                          setPartialState({
                            selectedBusinessUnit: e.target.value,
                          })
                        }>
                        <option value=''>
                          {t('payments.filters.businessUnitPlaceholder')}
                        </option>
                        {filteredBusinessUnits?.map((businessUnit) => (
                          <option key={businessUnit.id} value={businessUnit.id}>
                            {businessUnit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span className='search-icon'></span>
                  </div>
                </div>
              )}
              <div
                className='t-header-right'
                style={{
                  maxWidth: '200px',
                }}>
                <div className='custom-search-box'>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: '10px',
                      alignItems: 'center',
                    }}>
                    <div
                      className='custom-select'
                      style={{ marginTop: 0, paddingBottom: 0 }}>
                      {(role === 'admin' ||
                        checkPermission({
                          parentKey: 'transaction_page',
                          childKey: 'refund_information',
                          permission: 'download',
                        })) && (
                        <Button
                          className='full-width-elem'
                          style={{
                            height: '40px',
                          }}
                          onClick={handleClickDownload}
                          color='primary'>
                          {t('payments.buttons.download')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className='t-header-right'>
                {selectedPayments.length ? (
                  <h4 style={{ margin: '-20px 0 0 0' }}>
                    {t('payments.selection.summary', {
                      count: selectedPayments.length,
                      amount: parseFloat(
                        selectedPayments.reduce(
                          (prev, cur) => prev + cur.refund_amount,
                          0,
                        ),
                      ).toFixed(2),
                    })}
                  </h4>
                ) : (
                  ''
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <CheckboxDropdown
                options={dataMappingOptions}
                onChange={(selectedValues) =>
                  setPartialState({
                    selectedDataMappings: selectedValues.map(
                      (item) => item.value,
                    ),
                  })
                }
              />
              <div className='custom-search-box'>
                <div>
                  <select
                    style={{ width: '200px' }}
                    value={quickFilter}
                    id='status'
                    className='full-width-elem'
                    onChange={onQuickFilterChange}>
                    <option value=''>
                      {t('payments.quickFilters.placeholder')}
                    </option>
                    <option value='All Returned'>
                      {t('payments.quickFilters.allReturned')}
                    </option>
                    <option value='All Voided'>
                      {t('payments.quickFilters.allVoided')}
                    </option>
                    {role === 'admin' && (
                      <option value='Flagged Records'>
                        {t('payments.quickFilters.flaggedRecords')}
                      </option>
                    )}
                    {(isInsuranceEnabled || role === 'admin') && (
                      <option value='Payee Exceptions'>
                        {t('payments.quickFilters.payeeExceptions')}
                      </option>
                    )}
                    {(isInsuranceEnabled || role === 'admin') && (
                      <option value='Payee Exceptions with PDF'>
                        {t('payments.quickFilters.payeeExceptionsWithPdf')}
                      </option>
                    )}
                    {(isInsuranceEnabled || role === 'admin') && (
                      <option value='Addenda Missing'>
                        {t('payments.quickFilters.addendaMissing')}
                      </option>
                    )}
                    <option value='Invalid Address'>
                      {t('payments.quickFilters.invalidAddress')}
                    </option>
                  </select>
                </div>
                <span className='search-icon'></span>
              </div>
            </div>
            <Table width='100%' id='template'>
              <TableHead>
                <TableRow style={{ cursor: 'pointer', userSelect: 'none' }}>
                  <TableCell onClick={() => sortByColumn('created_at')}>
                    {t('payments.table.dateProcessed')}{' '}
                    {sortArrow('created_at')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('status_updatedat')}>
                    {t('payments.table.lastUpdated')}{' '}
                    {sortArrow('status_updatedat')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('last_name')}>
                    {t('payments.table.customerInfo')} {sortArrow('last_name')}
                  </TableCell>

                  <TableCell onClick={() => sortByColumn('facility_name')}>
                    {t('payments.table.facility')}
                    {sortArrow('facility_name')}
                  </TableCell>

                  <TableCell onClick={() => sortByColumn('refund_amount')}>
                    {t('payments.table.amount')}
                    {sortArrow('refund_amount')}
                  </TableCell>
                  {(allowedRefundTypes.length > 1 || role === 'admin') && (
                    <TableCell onClick={() => sortByColumn('refund_type')}>
                      {t('payments.table.payeeCategory')}
                      {sortArrow('refund_type')}
                    </TableCell>
                  )}
                  <TableCell
                    onClick={() => sortByColumn('payee_account_number')}>
                    {t('payments.table.customerIds')}
                    {sortArrow('payee_account_number')}
                  </TableCell>
                  {(role === 'admin' ||
                    checkOrganizationFeatures(
                      ORGANIZATION_FEATURES.showBusinessUnitsPage,
                    )) && (
                    <TableCell
                      onClick={() => sortByColumn('business_unit_name')}>
                      {t('payments.table.businessUnit')}
                      {sortArrow('business_unit_name')}
                    </TableCell>
                  )}
                  <TableCell>
                    {t('payments.table.paymentCategoryStatus')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_status')}>
                    {t('payments.table.refundStatus')}
                    {sortArrow('refund_status')}
                  </TableCell>
                  <TableCell>{t('payments.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              {payments && payments.length > 0 ? (
                <TableBody>
                  {payments.map((refund, index) => (
                    <StyledTableRow
                      payment={refund}
                      role={role}
                      className='txtalign'
                      style={{
                        backgroundColor:
                          role === 'admin' && refund.is_deleted
                            ? '#FFFACD'
                            : (refund.is_error ||
                                refund.transaction_is_error) &&
                              role === 'admin'
                            ? '#eaa4a4'
                            : '',
                      }}
                      key={index}>
                      <TableCell>
                        {utcToLocal(refund.created_at, 'MM/DD/YYYY')}
                        <br />
                        <span title={refund.refund_file_name}>
                          <Typography fontSize={'14px'}>
                            {refund.tags?.includes(SYSTEM_TAGS.MANUAL_ENTRY)
                              ? t('payments.table.manuallyCreated')
                              : refund.refund_file_name
                              ? `${refund.refund_file_name.slice(
                                  0,
                                  7,
                                )}...${refund.refund_file_name.slice(-7)}`
                              : 'N/A'}
                          </Typography>
                        </span>
                      </TableCell>
                      <TableCell>
                        {refund.status_updatedat
                          ? utcToLocal(refund.status_updatedat, 'MM/DD/YYYY')
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {refund.first_name} {refund.last_name} {}
                        <a
                          title='Edit Customer Information'
                          onClick={() => {
                            setCurrentInfo(refund);
                            setOriginalInfo(refund);
                            setModalErr('');
                          }}>
                          <EditRounded
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            fontSize='inherit'
                          />
                        </a>
                        {role !== 'admin' &&
                          checkPermission({
                            parentKey: 'transaction_page',
                            childKey: 'send_payment',
                            permission: 'read',
                          }) && (
                            <a
                              title='Send Payment'
                              onClick={() => {
                                setCurrentCheckRefund(refund);
                              }}>
                              <AttachMoneyRounded
                                style={{
                                  cursor: 'pointer',
                                  fontSize: '18px',
                                }}
                                fontSize='inherit'
                              />
                            </a>
                          )}
                        {isCustomPaymentTypesEnabled ? (
                          refund.entity_type === ENTITY_TYPE.INDIVIDUAL &&
                          refund.email ? (
                            <p style={{ fontSize: '12px' }}>({refund.email})</p>
                          ) : refund.entity_type === ENTITY_TYPE.BUSINESS &&
                            refund.business_payee_name ? (
                            <p style={{ fontSize: '12px' }}>
                              ({refund.business_payee_name})
                            </p>
                          ) : null
                        ) : refund.refund_type === REFUND_TYPES.REGULAR &&
                          refund.email ? (
                          <p style={{ fontSize: '12px' }}>({refund.email})</p>
                        ) : (refund.refund_type === REFUND_TYPES.BUSINESS ||
                            refund.refund_type === REFUND_TYPES.INSURANCE) &&
                          refund.business_payee_name ? (
                          <p style={{ fontSize: '12px' }}>
                            ({refund.business_payee_name})
                          </p>
                        ) : null}
                        {refund.echeck_location && (
                          <p
                            style={{
                              fontWeight: 'bold',
                              fontSize: '12px',
                              cursor: 'pointer',
                              color: '#3AB795',
                              margin: 0,
                            }}
                            onClick={() =>
                              setSelectedECheckRefundId(refund.id)
                            }>
                            {t('payments.table.viewECheck', 'View eCheck')}
                          </p>
                        )}
                        {refund.has_files_attached && (
                          <p
                            style={{
                              fontWeight: 'bold',
                              fontSize: '12px',
                            }}>
                            {t('payments.table.pdfsAttached')}
                          </p>
                        )}
                      </TableCell>

                      <TableCell>{refund.facility_name}</TableCell>

                      <TableCell>
                        $
                        {parseFloat(
                          refund && refund.refund_amount,
                        ).toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}{' '}
                      </TableCell>
                      {(allowedRefundTypes.length > 1 || role === 'admin') && (
                        <TableCell>
                          {role === 'admin'
                            ? refund.custom_payment_type ||
                              refundTypeToLabel[refund.refund_type] ||
                              'N/A'
                            : isCustomPaymentTypesEnabled
                            ? refund.custom_payment_type || 'N/A'
                            : refundTypeToLabel[refund.refund_type] || 'N/A'}
                          <br />({refund.entity_type || 'N/A'})
                        </TableCell>
                      )}
                      <TableCell>
                        {refund.payee_account_number || 'N/A'} <br />
                        {refund.client_transaction_id || 'N/A'} <br />
                        {refund.id || 'N/A'}
                      </TableCell>
                      {(role === 'admin' ||
                        checkOrganizationFeatures(
                          ORGANIZATION_FEATURES.showBusinessUnitsPage,
                        )) && (
                        <TableCell>
                          {refund.business_unit_name || 'N/A'} <br />
                          {refund.business_unit_identifier || 'N/A'}
                        </TableCell>
                      )}
                      <TableCell align='center'>
                        {role === 'admin' && (
                          <AchReturnedChip payment={refund} role={role} />
                        )}
                        {refund.transaction_status === 'ACH-returned' &&
                        role === 'admin'
                          ? ''
                          : refund.refund_method
                          ? refund.refund_method_string
                          : 'N/A'}
                        {refund.transaction_status === 'ACH-returned' &&
                        role !== 'admin' ? (
                          <>
                            <br />
                            <span
                              style={{
                                fontSize: '12px',
                                color:
                                  refund.transaction_status === 'ACH-returned'
                                    ? '#ff0000'
                                    : '',
                              }}>
                              {getTranslatedStatus(refund.transaction_status) ||
                                ' '}
                            </span>
                          </>
                        ) : (
                          refund.transaction_status !== 'ACH-returned' && (
                            <>
                              <br />
                              <span style={{ fontSize: '12px' }}>
                                {' '}
                                {getTranslatedStatus(
                                  refund.transaction_status,
                                ) || ' '}
                              </span>
                            </>
                          )
                        )}
                        {refund.transaction_status && <br />}
                        {![REFUND_METHOD.VENMO, REFUND_METHOD.PAYPAL].includes(
                          refund.refund_method,
                        ) && (
                          <span style={{ fontSize: '12px' }}>
                            {refund?.additional_info ||
                            refund?.refund_additional_info
                              ? `(${
                                  refund?.additional_info ||
                                  refund?.refund_additional_info
                                })`
                              : ''}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* {obj && obj.payment_status} */}
                        {refund && refund.refund_status && (
                          <span>
                            <a
                              style={{
                                color:
                                  refund.refund_status ===
                                  REFUND_STATUSES.COMPLETED
                                    ? ''
                                    : '#000000',

                                cursor: 'auto',
                              }}>
                              {refund.refund_status
                                ? refund.refund_status
                                : 'N/A'}
                            </a>
                            <br />
                            <span style={{ fontSize: '12px' }}>
                              {(refund && refund.refund_sub_status) || 'N/A'}
                            </span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            gap: '2px',
                            alignItems: 'center',
                          }}>
                          <HistoryRounded
                            style={{ color: '#3AB795' }}
                            title='Status History'
                            onClick={() => setSelectedRefund(refund.id)}
                          />

                          <LongMenu
                            type={role}
                            handleCopyToClipboard={() =>
                              copyLinkToClipBoard(refund.id)
                            }
                            handleRefundHistory={() => {
                              setSelectedForTrackChangesId(refund.id);
                              getInfoHistory(refund.id, refund);
                            }}
                            statusAction={statusAction}
                            toggleDelete={() => toggleDelete(refund.id)}
                            refund={refund}
                            sendCheck={() => {
                              setCurrentCheckRefund(refund);
                            }}
                          />
                        </div>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {t('payments.table.noTransactionDataFound')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </div>
        </CardBody>
      </Card>
      <StatusHistoryModal
        role={role}
        open={selectedRefund !== null}
        statusHistory={statusHistory}
        onRetryPullingAch={handleRetryPullingAch}
        onClose={() => setSelectedRefund(null)}
        refund={payments.find((r) => r.id === selectedRefund)}
      />
      {currentCheckRefund && (
        <IssueCheckModal
          currentCheckRefund={currentCheckRefund}
          setCurrentCheckRefund={setCurrentCheckRefund}
          setCurrentInfo={setCurrentInfo}
          setOriginalInfo={setOriginalInfo}
          setModalErr={setModalErr}
          processPayment={processPayment}
          setPartialState={setPartialState}
        />
      )}

      {currentInfo &&
        (isCustomPaymentTypesEnabled ? (
          <EditManualRefundModal
            refund={currentInfo}
            onClose={() => {
              setCurrentInfo(null);
              setOriginalInfo(null);
              setModalErr('');
            }}
            path={'edit-refund'}
            updateStatus={false}
            reFetch={refetchRefunds}
          />
        ) : (
          <UpdateRefundModal
            setModalErr={setModalErr}
            currentInfo={currentInfo}
            originalInfo={originalInfo}
            modalErr={modalErr}
            onInputChange={onInputChange}
            setCurrentInfo={setCurrentInfo}
            setOriginalInfo={setOriginalInfo}
            role={role}
            getRefunds={refetchRefunds}
            payments={payments}
            currentCheckRefund={currentCheckRefund}
            setCurrentCheckRefund={setCurrentCheckRefund}
          />
        ))}
      {selectedForTrackChangesId && (
        <RefundInfoHistory
          selectedForTrackChangesId={selectedForTrackChangesId}
          setSelectedForTrackChangesId={setSelectedForTrackChangesId}
          infoHistory={infoHistory}
          getClass={getClass}
        />
      )}
      <ConfirmationModal
        open={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setCancelComment('');
          setCancelRefundId(null);
        }}
        onConfirm={confirmCancelRefund}>
        <div>
          <div>{t('payments.messages.cancelRefundConfirmation')}</div>
          <div style={{ marginTop: 10 }}>
            <CustomInput
              labelText={
                <>
                  {t('payments.fields.comment')}
                  <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                </>
              }
              id='comment'
              name='comment'
              value={cancelComment}
              onChange={(e) => setCancelComment(e.target.value)}
              formControlProps={{ fullWidth: true }}
              error={!cancelComment}
              inputProps={{
                helperText: !cancelComment
                  ? t('payments.validation.commentRequired')
                  : '',
              }}
              helperTextStyle={{ color: 'red' }}
            />
          </div>
        </div>
      </ConfirmationModal>

      <CancelPaymentDialog
        open={cancelPaymentModalOpen}
        onClose={() => {
          setCancelPaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        onConfirm={handleCancelPaymentConfirm}
        paymentId={selectedPayment?.id}
        organizationId={selectedPayment?.organization_id}
        payment={selectedPayment}
      />

      <ECheckPreviewModal
        open={selectedECheckRefundId !== null}
        onClose={() => setSelectedECheckRefundId(null)}
        refundId={selectedECheckRefundId}
      />
    </div>
  );
}
